import type {
  Discrepancy,
  OrderResponse,
  PayoutResponse,
  ReconciliationReport,
  StatementResponse,
  StoreResponse,
  Verdict,
} from '@clarity/contract';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDerivedSummary,
  useOrders,
  usePayouts,
  useReconciliationByPayout,
  useReconciliationByPeriod,
  useStatements,
  useSyncStore,
} from '../../queries/index.js';
import { Modal } from '../Modal.js';
import { PayoutMatchPanel } from '../payouts/PayoutMatchPanel.js';
import { filterPaidAt, isEmptyRange } from '../payouts/paidAtFilter.js';
import { QueryState } from '../QueryState.js';
import {
  accuracyLabel,
  isNotReconciled,
  isOnboardingRequired,
  money,
  num,
  period,
  shortDate,
  verdictLabel,
} from '../format.js';

/**
 * Shared store-data building blocks (clarity-orders-detail task 4.1). Orders /
 * Statements / Payouts are now STANDALONE pages ({@link StoreOrders},
 * {@link StoreStatements}, {@link StorePayouts}) rather than tabs on one
 * `StoreData` host — but their badges, the per-store "Sync now" header, and the
 * Statements / Payouts BODIES are reused verbatim across the standalone pages
 * and the combined (All / Brand) per-branch sections. They live here so a single
 * definition feeds every consumer.
 *
 * The badges + `OnboardingBlocked` keep their previous names and signatures so
 * their existing importers (`CombinedOrders` et al.) are unchanged apart from the
 * module path.
 */

// ─── The per-store header (Sync now) ──────────────────────────────────────────

/**
 * A compact, unobtrusive "Sync now" control for the standalone data pages
 * (clarity-onboarding-ingestion). The heavy "Data — {id}" header box was retired
 * (the store identity already lives in the rail + topbar scope); this leaves just
 * the manual sync trigger, right-aligned above the page body. Ingestion is
 * always-on server-side — this only forces an immediate pull.
 */
export function SyncControl({ store }: { store: StoreResponse }) {
  const sync = useSyncStore(store.id);
  return (
    <div className="data-sync">
      <button
        type="button"
        className="ghost data-sync__btn"
        onClick={() => sync.mutate({ params: { id: store.id }, body: {} })}
        disabled={sync.isPending}
      >
        {sync.isPending ? 'Syncing…' : 'Sync now'}
      </button>
    </div>
  );
}

// ─── Badges ───────────────────────────────────────────────────────────────────

/**
 * A short badge for the data-completeness axis (Req 9.1), labelled with the
 * user-facing accuracy name (clarity-reconciliation Req 4.1): Pending /
 * Platform estimated / Finalized / AI estimated. Colour is not the only signal —
 * the text label carries the state (WCAG; DESIGN.md a11y).
 */
export function AccuracyBadge({ accuracy }: { accuracy: OrderResponse['accuracy'] }) {
  return (
    <span className={`badge badge-accuracy-${accuracy}`}>{accuracyLabel(accuracy)}</span>
  );
}

/**
 * The reconciliation verdict chip (clarity-reconciliation Req 7.4). The engine's
 * `reconciled` reuses the design's `--verified` styling; a word plus the colour
 * state keeps colour from being the only signal. `role="status"` so the verdict
 * is announced when it resolves.
 */
export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const style = verdict === 'reconciled' ? 'verified' : verdict;
  return (
    <span className={`verdict verdict--${style}`} role="status">
      {verdictLabel(verdict)}
    </span>
  );
}

/** A short badge for the settlement axis (Req 8.1). */
export function SettlementBadge({
  settlement,
}: {
  settlement: OrderResponse['settlementStatus'];
}) {
  return <span className={`badge badge-settlement-${settlement}`}>{settlement}</span>;
}

/** A profile grid row (label / value), used by the order detail economics grids. */
export function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile-row">
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

/**
 * A group-panel heading in the uppercase stat-label voice — the same idiom the
 * derived summary's grouped glass panels use, so the order detail reads as part
 * of the same Operate world.
 */
export function SummaryGroupHead({ title }: { title: string }) {
  return <h5 className="summary-group__head">{title}</h5>;
}

/** Shown when a read is blocked because the store is not yet onboarded (Req 1.2). */
export function OnboardingBlocked() {
  return (
    <p role="alert" className="error">
      This store is not onboarded yet — complete onboarding to view its data.
    </p>
  );
}

// ─── Statements (Req 14.2) ────────────────────────────────────────────────────

export function StatementsView({ store }: { store: StoreResponse }) {
  const statements = useStatements(store.id);
  const rows = statements.data?.status === 200 ? statements.data.body : [];

  if (isOnboardingRequired(statements.error)) return <OnboardingBlocked />;

  return (
    <QueryState
      isLoading={statements.isLoading}
      isError={statements.isError}
      label="statements"
    >
      {rows.length === 0 ? (
        <p className="muted">No statements yet.</p>
      ) : (
        <table className="data-table" aria-label="Statements">
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">Gross</th>
              <th scope="col">Commission</th>
              <th scope="col">Marketing</th>
              <th scope="col">Net payout</th>
              <th scope="col">Reconciliation</th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <StatementRow key={s.id} store={store} statement={s} />
            ))}
          </tbody>
        </table>
      )}
    </QueryState>
  );
}

/**
 * One statement row + its period lattice verdict (clarity-reconciliation Req
 * 7.4, 8.4). Each statement is per-period, so the period reconciliation report
 * is fetched by its `[periodStart, periodEnd)` window; a `404` is the expected
 * "not reconciled yet" state, surfaced as a muted note (not a crash).
 */
function StatementRow({
  store,
  statement,
}: {
  store: StoreResponse;
  statement: StatementResponse;
}) {
  const recon = useReconciliationByPeriod(store.id, statement.periodStart, statement.periodEnd);
  const report = recon.data?.status === 200 ? recon.data.body : undefined;
  return (
    <tr>
      <td>{period(statement.periodStart, statement.periodEnd)}</td>
      <td>{money(statement.totalGross)}</td>
      <td>{money(statement.totalCommission)}</td>
      <td>{money(statement.totalMarketingFees)}</td>
      <td>{money(statement.totalNetPayout)}</td>
      <td>
        <PeriodVerdictCell
          report={report}
          isLoading={recon.isLoading}
          notReconciled={isNotReconciled(recon.error)}
          isError={recon.isError && !isNotReconciled(recon.error)}
        />
      </td>
      <td>
        <span className="badge">{statement.source}</span>
        {statement.estimated && <span className="badge badge-unresolved">estimated</span>}
      </td>
    </tr>
  );
}

/** The compact period-verdict cell for a statement row (verdict + any gaps). */
function PeriodVerdictCell({
  report,
  isLoading,
  notReconciled,
  isError,
}: {
  report: ReconciliationReport | undefined;
  isLoading: boolean;
  notReconciled: boolean;
  isError: boolean;
}) {
  if (isLoading) return <span className="muted">Checking…</span>;
  if (isError) return <span className="muted">Unavailable</span>;
  if (notReconciled || !report) return <span className="muted">Not reconciled yet</span>;
  return (
    <div className="period-verdict">
      <VerdictBadge verdict={report.verdict} />
      {report.verdict === 'incomplete' && report.missingGrains.length > 0 && (
        <span className="cell-sub">Missing: {report.missingGrains.join(', ')}</span>
      )}
      {report.verdict === 'discrepant' && report.discrepancies.length > 0 && (
        <span className="cell-sub">
          {num(report.discrepancies.length)} discrepanc
          {report.discrepancies.length === 1 ? 'y' : 'ies'}
        </span>
      )}
    </div>
  );
}

// ─── Payouts (Req 14.2) ───────────────────────────────────────────────────────

/**
 * The single-store Payouts body. DUAL-SOURCE by design (see design.md "Why two
 * data sources"):
 *
 * - the month-grouped {@link PayoutMatchPanel} is driven by the DERIVED SUMMARY
 *   (`useDerivedSummary`) — the only source that carries the month grouping +
 *   opening/closing balances computed in `@clarity/core`;
 * - the Details MODAL is driven by the RAW payouts (`usePayouts`) + orders
 *   (`useOrders`), because `PayoutRowData` doesn't carry `linkedOrderIds`,
 *   `vouchersByBrand`, `marketingAds`, or the DB `id` the modal needs.
 *
 * Two additive, opt-in capabilities (both default off, so the combined surface
 * and onboarding renders are unchanged):
 * - `onOpenPayout` present ⇒ the panel rows get a Details affordance. NOTE: the
 *   prop is only an OPT-IN SIGNAL — its body is irrelevant. PayoutsView owns the
 *   id mapping and hands the panel its OWN `handleOpenPayout` (externalPayoutId →
 *   raw uuid → `onTogglePayout`). See the id-mapping block below.
 * - `withFilter` ⇒ a paid-at From/To filter is applied to the panel input (the
 *   filter UI + honesty note + distinct empty branches are TASK 4.2).
 */
export function PayoutsView({
  store,
  openPayoutId,
  onTogglePayout,
  withFilter = false,
  onOpenPayout,
  from = null,
  to = null,
  onRangeChange,
}: {
  store: StoreResponse;
  openPayoutId: string | null;
  onTogglePayout: (id: string | null) => void;
  withFilter?: boolean;
  /**
   * OPT-IN SIGNAL for the per-row Details affordance. Its presence (not its body)
   * is what matters: PayoutsView maps the panel's `externalPayoutId` to the raw
   * payout `id` itself and flips `?payout=` via `onTogglePayout`. The standalone
   * page passes any non-undefined callback to opt in; the combined surface omits
   * it so the panel renders no Details control.
   */
  onOpenPayout?: (externalPayoutId: string) => void;
  from?: string | null;
  to?: string | null;
  onRangeChange?: (from: string | null, to: string | null) => void;
}) {
  // DERIVED SUMMARY — drives the month-grouped panel + the onboarding gate. Body
  // is a `DerivedRead` on 200, or `null` when no reference data is ingested yet.
  const persisted = useDerivedSummary(store.id, store.referenceDataPresent);
  const derivedBody = persisted.data?.status === 200 ? persisted.data.body : null;

  // RAW payouts + orders — the MODAL path ONLY (kept exactly as before). The raw
  // rows also back the externalPayoutId → id map the panel's Details uses.
  const payouts = usePayouts(store.id);
  const payoutsData = payouts.data;
  const rawRows = payoutsData?.status === 200 ? payoutsData.body : [];

  // The store's orders, fetched ONCE and indexed by id, so the open payout's
  // modal can resolve its linked orders (`linkedOrderIds`) for the drill-down
  // without a per-payout query. The list read returns `{ order, economics }`.
  const orders = useOrders(store.id);
  const orderData = orders.data;
  const ordersById = useMemo(() => {
    const list = orderData?.status === 200 ? orderData.body : [];
    return new Map(list.map((r) => [r.order.id, r.order]));
  }, [orderData]);

  // ID MAPPING (design.md "The id mapping"): the panel rows are keyed by
  // `externalPayoutId`; the modal + `?payout=` use the raw payout's uuid `id`. So
  // the panel's callback carries `externalPayoutId`, and PayoutsView resolves it
  // to the raw payout before flipping the `?payout=` sub-state.
  const payoutByExternalId = useMemo(() => {
    const list = payoutsData?.status === 200 ? payoutsData.body : [];
    return new Map(list.map((p) => [p.externalPayoutId, p] as const));
  }, [payoutsData]);

  const handleOpenPayout = (externalPayoutId: string) => {
    const raw = payoutByExternalId.get(externalPayoutId);
    if (raw) onTogglePayout(raw.id);
  };

  // Open a linked order in the Orders surface (same store scope + `?order=`), the
  // reverse of the order→payout trace. The Payouts page lives at a store scope,
  // so the Orders surface is a sibling under the same `/store/:id` base.
  const navigate = useNavigate();
  const { clientId } = useParams();
  const openOrder = (orderId: string) => {
    if (!clientId) return;
    navigate(`/c/${clientId}/store/${store.id}/orders?order=${orderId}`);
  };

  // The modal is driven by the RAW payout keyed by uuid `id` (not the panel row).
  const openPayout = openPayoutId
    ? rawRows.find((pay) => pay.id === openPayoutId)
    : undefined;

  // The derived read is the PRIMARY driver of the gate (Req 1.7). A 409 can also
  // surface on `usePayouts`; this early-return keeps that harmless.
  if (isOnboardingRequired(persisted.error)) return <OnboardingBlocked />;
  if (isOnboardingRequired(payouts.error)) return <OnboardingBlocked />;

  // The standalone-only paid-at filter is applied to the panel input only (the
  // modal path reads raw payouts regardless). `null` ⇒ no reference data (1.8).
  const payoutRows = derivedBody ? derivedBody.derived.payouts : null;
  const panelPayouts =
    payoutRows !== null && withFilter ? filterPaidAt(payoutRows, { from, to }) : payoutRows;

  // The filter is "active" only on the standalone surface AND when at least one
  // bound is set — an all-empty range is a no-op (Req 3.10). While active, the
  // honesty note is always shown (Req 3.7) and the from>to / zero-in-range cases
  // get a DISTINCT message (Req 3.8, 3.9) rather than the panel's own empty state.
  const filterActive = withFilter && !isEmptyRange({ from, to });

  // "Nothing survived the active filter" (covers from>to too, which yields zero
  // rows). Distinct from "the store has no derived payouts at all" (payoutRows
  // === null → the no-reference-data branch) and from the panel's own built-in
  // unfiltered empty copy.
  const filteredEmpty =
    filterActive &&
    panelPayouts !== null &&
    panelPayouts.rows.length === 0 &&
    panelPayouts.statementChecks.length === 0;

  return (
    <QueryState isLoading={persisted.isLoading} isError={persisted.isError} label="payouts">
      {derivedBody === null || panelPayouts === null ? (
        // 200 with a null body: reference data hasn't been ingested yet (Req 1.8).
        // The filter never renders here — there is simply no payouts surface yet.
        <p className="muted">
          No reference data yet — complete onboarding to see this store&apos;s payouts.
        </p>
      ) : (
        <>
          {/* Paid-at From/To filter — standalone surface only (Req 3.1/3.2). Each
              control is a visibly-labelled native date input (idiom shared with
              onboarding's "Effective from"); `<input type="date">` yields
              `YYYY-MM-DD`, exactly what `from`/`to` + `filterPaidAt` expect. The
              lifted range is owned by StorePayouts via `onRangeChange` (task 5). */}
          {withFilter && (
            <div
              className="payouts-filter"
              role="group"
              aria-label="Filter payouts by paid date"
            >
              <label>
                Paid from
                <input
                  type="date"
                  value={from ?? ''}
                  onChange={(e) => onRangeChange?.(e.target.value || null, to)}
                />
              </label>
              <label>
                Paid to
                <input
                  type="date"
                  value={to ?? ''}
                  onChange={(e) => onRangeChange?.(from, e.target.value || null)}
                />
              </label>
            </div>
          )}

          {/* Honesty note (Req 3.7): the per-month opening/closing figures come
              from `@clarity/core` over the FULL statement month and are never
              recomputed — the filter only ever hides rows. Kept mounted in the
              zero-in-range branch below so the owner can widen the range. */}
          {filterActive && (
            <p className="muted" role="note">
              Each month&apos;s closing and opening figures reflect the full statement
              month, not just the payouts shown here.
            </p>
          )}

          {filteredEmpty ? (
            // DISTINCT from the panel's generic unfiltered empty copy (Req 3.8/3.9).
            // Covers from>to and any zero-survivor range. The filter controls + note
            // above stay mounted so the range can be widened without a dead end.
            <p className="muted payouts__empty" role="note">
              No payouts paid in this range.
            </p>
          ) : (
            <PayoutMatchPanel
              payouts={panelPayouts}
              estimate={derivedBody.derived.estimatedBalance}
              currency={store.profile.currency}
              // The panel's Details affordance is OPT-IN on the presence of the
              // caller's `onOpenPayout` prop. When present we hand it OUR mapping
              // handler (externalPayoutId → raw id → onTogglePayout); the caller's
              // own body is never invoked. When absent, the panel renders no Details.
              onOpenPayout={onOpenPayout ? handleOpenPayout : undefined}
            />
          )}
        </>
      )}

      {/* The "Details" affordance opens a MODAL with the full payout view (figures,
          vouchers, the PAGED orders-settled list, and the reconciliation verdict),
          mirroring the onboarding "Merge into…" dialog. `?payout=` drives it, so a
          shared link / the order→payout trace reopens it. */}
      {openPayout && (
        <PayoutDetailModal
          store={store}
          payout={openPayout}
          ordersById={ordersById}
          onOpenOrder={openOrder}
          onClose={() => onTogglePayout(null)}
        />
      )}
    </QueryState>
  );
}

/** How many settled orders a modal page shows before the pager kicks in. */
const ORDERS_PAGE_SIZE = 10;

/**
 * The payout DETAIL MODAL (clarity-orders-detail): payout-level settled figures,
 * vouchers, the PAGED orders-settled list (each row drills into its order
 * detail), and the latest reconciliation verdict. The reconciliation read fires
 * only while the modal is open. Everything is read from data already fetched —
 * nothing is recomputed in the UI. Focus-return to the triggering row button is
 * handled by the browser since the button stays mounted under the table.
 */
function PayoutDetailModal({
  store,
  payout,
  ordersById,
  onOpenOrder,
  onClose,
}: {
  store: StoreResponse;
  payout: PayoutResponse;
  ordersById: Map<string, OrderResponse>;
  onOpenOrder: (orderId: string) => void;
  onClose: () => void;
}) {
  // The reconciliation report for this payout (read-only). Fetched while open; a
  // `404` is the expected "not reconciled yet" state, surfaced as a note.
  const recon = useReconciliationByPayout(store.id, payout.id, true);
  const report = recon.data?.status === 200 ? recon.data.body : undefined;
  const notReconciled = isNotReconciled(recon.error);
  const reconError = recon.isError && !notReconciled;

  const vouchers = Object.entries(payout.vouchersByBrand);
  const settledCount = payout.linkedOrderIds.length;
  const expected = payout.expectedOrderCount;
  const coverageShort = expected !== null && settledCount < expected;

  // Paged orders-settled list (Req: the orders must be paged). One page at a time
  // over the payout's linkedOrderIds, resolved against the loaded orders.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(settledCount / ORDERS_PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const pageIds = payout.linkedOrderIds.slice(
    current * ORDERS_PAGE_SIZE,
    current * ORDERS_PAGE_SIZE + ORDERS_PAGE_SIZE,
  );
  const rangeFrom = settledCount === 0 ? 0 : current * ORDERS_PAGE_SIZE + 1;
  const rangeTo = Math.min(settledCount, (current + 1) * ORDERS_PAGE_SIZE);

  return (
    <Modal
      title={<>Payout {payout.externalPayoutId}</>}
      onClose={onClose}
      className="modal payout-modal"
    >
      {/* Grouped glass panels mirroring OrderDetailBody: Settlement + Coverage +
          (optional) Vouchers by brand sit in a responsive grid; the paged
          orders-settled table and the reconciliation read span full width. */}
      <div className="summary-groups__grid payout-modal__grid">
        {/* Settlement — the net line + what it is built from. */}
        <section className="card summary-group" aria-label="Settlement">
          <SummaryGroupHead title="Settlement" />
          <div className="stat">
            <span className="stat__label">Net payout</span>
            <span className="stat__value num">{money(payout.netPayout)}</span>
          </div>
          <dl className="profile-grid">
            <Row label="Gross sales" value={money(payout.grossSales)} />
            <Row label="Commissions" value={money(payout.commissions)} />
            <Row label="Fees" value={money(payout.fees)} />
            <Row label="Marketing / ads" value={money(payout.marketingAds)} />
          </dl>
        </section>

        {/* Coverage — how many orders this payout settled, and when/for which period. */}
        <section className="card summary-group" aria-label="Coverage">
          <SummaryGroupHead title="Coverage" />
          <div className="stat">
            <span className="stat__label">Orders settled</span>
            <span className="stat__value num">
              {expected !== null
                ? `${num(settledCount)} of ${num(expected)}`
                : num(settledCount)}
            </span>
          </div>
          <dl className="profile-grid">
            <Row label="Paid on" value={payout.paidAt ? shortDate(payout.paidAt) : '—'} />
            <Row label="Period" value={period(payout.periodStart, payout.periodEnd)} />
          </dl>
          {coverageShort && (
            <p className="muted payout-modal__coverage" role="note">
              {num(expected! - settledCount)} of the payout&apos;s {num(expected!)} reported
              orders are not yet linked.
            </p>
          )}
        </section>

        {vouchers.length > 0 && (
          <section className="card summary-group" aria-label="Vouchers by brand">
            <SummaryGroupHead title="Vouchers by brand" />
            <dl className="profile-grid">
              {vouchers.map(([brandName, amount]) => (
                <Row key={brandName} label={brandName} value={money(amount)} />
              ))}
            </dl>
          </section>
        )}
      </div>

      {/* The orders this payout settled — PAGED; each drills into its order detail. */}
      <section className="card summary-group summary-group--wide" aria-label="Orders settled">
        <SummaryGroupHead title={`Orders settled (${num(settledCount)})`} />
        {settledCount === 0 ? (
          <p className="muted">No orders linked to this payout yet.</p>
        ) : (
          <>
            <table className="data-table" aria-label="Orders settled by this payout">
              <thead>
                <tr>
                  <th scope="col">Order</th>
                  <th scope="col">Date</th>
                  <th scope="col">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {pageIds.map((id) => {
                  const order = ordersById.get(id);
                  return (
                    <tr key={id}>
                      {order ? (
                        <>
                          <td>
                            <button
                              type="button"
                              className="link"
                              onClick={() => onOpenOrder(order.id)}
                              aria-label={`Open order ${order.externalOrderId}`}
                            >
                              {order.externalOrderId}
                            </button>
                          </td>
                          <td className="muted">{shortDate(order.timestamp)}</td>
                          <td className="num">{money(order.subtotal, order.currency)}</td>
                        </>
                      ) : (
                        <td className="muted" colSpan={3}>
                          Order unavailable
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pageCount > 1 && (
              <nav className="pager" aria-label="Orders settled pages">
                <button
                  type="button"
                  className="ghost"
                  onClick={() => setPage(current - 1)}
                  disabled={current === 0}
                >
                  Previous
                </button>
                <span className="pager__status" role="status">
                  {num(rangeFrom)}–{num(rangeTo)} of {num(settledCount)}
                </span>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => setPage(current + 1)}
                  disabled={current >= pageCount - 1}
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </section>

      {/* The latest reconciliation verdict (read-only; may not exist yet). */}
      <section className="card summary-group summary-group--wide" aria-label="Reconciliation">
        <SummaryGroupHead title="Reconciliation" />
        {recon.isLoading ? (
          <p role="status" className="muted">
            Loading reconciliation…
          </p>
        ) : notReconciled ? (
          <p className="muted">Not reconciled yet — no report for this payout.</p>
        ) : reconError ? (
          <p role="alert" className="error">
            Could not load reconciliation.
          </p>
        ) : report ? (
          <PayoutReconciliation report={report} />
        ) : (
          <p className="muted">Not reconciled yet.</p>
        )}
      </section>
    </Modal>
  );
}

/** The reconciliation verdict + discrepancy/missing-grain detail for a payout. */
function PayoutReconciliation({ report }: { report: ReconciliationReport }) {
  return (
    <div className="recon-detail">
      <div className="recon-detail__head">
        <VerdictBadge verdict={report.verdict} />
        <span className="muted">tolerance ±{report.tolerance}</span>
      </div>

      {report.verdict === 'reconciled' && (
        <p className="muted">Every check held within tolerance.</p>
      )}

      {report.verdict === 'incomplete' && (
        <p role="note">
          Incomplete — waiting on{' '}
          <strong>{report.missingGrains.join(', ') || 'a required grain'}</strong>.
        </p>
      )}

      {report.discrepancies.length > 0 && (
        <DiscrepancyList discrepancies={report.discrepancies} />
      )}
    </div>
  );
}

/** The per-check discrepancy table (check / expected / actual / delta / message). */
function DiscrepancyList({ discrepancies }: { discrepancies: Discrepancy[] }) {
  return (
    <table className="data-table discrepancy-table" aria-label="Discrepancies">
      <thead>
        <tr>
          <th scope="col">Check</th>
          <th scope="col">Expected</th>
          <th scope="col">Actual</th>
          <th scope="col">Delta</th>
          <th scope="col">Detail</th>
        </tr>
      </thead>
      <tbody>
        {discrepancies.map((d, idx) => (
          <tr key={`${d.check}-${idx}`}>
            <td>
              {d.check}
              <span className="cell-sub"> {d.kind}</span>
            </td>
            <td>{money(d.expected)}</td>
            <td>{money(d.actual)}</td>
            <td className={d.delta !== null && d.delta !== 0 ? 'delta-nonzero' : undefined}>
              {money(d.delta)}
            </td>
            <td className="discrepancy-message">{d.message}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The payout detail body — the figures for one payout (clarity-payouts-monthly-
 * view). Renders the payout's REAL persisted fields (net/gross/commissions/fees/
 * ads/period/linked orders) via the shared formatters; it does not re-derive or
 * invent any number. `onOpenOrder` opens a linked order.
 *
 * NOTE: the previous richer itemized breakdown (per-line voucher/additional-sales
 * table) was lost in a web-submodule revert; this restores a correct, data-true
 * summary so the page works. The breakdown table can be re-added from the
 * payout's `breakdown` / `vouchersByBrand` fields when that UI is rebuilt.
 */
export function PayoutDetailBody({
  store,
  payout,
  ordersById,
  onOpenOrder,
}: {
  store: StoreResponse;
  payout: PayoutResponse;
  ordersById: Map<string, OrderResponse>;
  onOpenOrder: (orderId: string) => void;
}) {
  void store;
  const linked = payout.linkedOrderIds ?? [];
  return (
    <div className="payout-detail">
      <dl className="subscription-stats">
        <Row label="Net payout" value={money(payout.netPayout)} />
        <Row label="Gross sales" value={money(payout.grossSales)} />
        <Row label="Commissions" value={money(payout.commissions)} />
        <Row label="Fees" value={money(payout.fees)} />
        <Row label="Marketing / ads" value={money(payout.marketingAds)} />
        <Row label="Period" value={period(payout.periodStart, payout.periodEnd)} />
        <Row label="Paid at" value={payout.paidAt ? shortDate(payout.paidAt) : '—'} />
        <Row label="Linked orders" value={num(linked.length)} />
      </dl>

      {linked.length > 0 && (
        <div className="payout-detail__orders">
          <h4>Linked orders</h4>
          <ul className="list">
            {linked.map((oid) => {
              const o = ordersById.get(oid);
              return (
                <li key={oid}>
                  <button type="button" className="sm ghost" onClick={() => onOpenOrder(oid)}>
                    {o?.externalOrderId ?? oid}
                  </button>
                  {o && <span className="muted num"> · {money(o.subtotal)}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
